import { API_BASE, ApiError } from "@/lib/apiClient";
import { api } from "@/lib/apiClient";
import { tenantSlug } from "@/lib/tenantSlug";

export interface TouristImportProgress {
  phase: string | null;
  percent: number;
  message: string | null;
}

export interface TouristImportFileCheck {
  name: string;
  present: boolean;
  encoding?: string | null;
  row_count?: number | null;
  columns_ok?: boolean | null;
  missing_columns?: string[];
  warnings?: string[];
}

export interface TouristMappingSuggestion {
  tourist_key: string;
  tourist_label: string;
  target_id: string;
  target_label: string;
  score: number;
  confirmed?: boolean;
}

export interface TouristPlace {
  tourist_plaid: number;
  tourist_planr: string;
}

export interface TouristArticle {
  tourist_artid: number;
  tourist_artnr?: string | null;
  tourist_bezeichnung?: string | null;
}

/** Kürzel aus dem Tourist-Dump (Platzkategorie adr14 oder Anreiseart adr22). */
export interface TouristCode {
  tourist_code: string;
  guest_count: number;
}

export interface TouristImportPreview {
  job_id: string;
  files: TouristImportFileCheck[];
  warnings: string[];
  tourist_places: TouristPlace[];
  tourist_articles: TouristArticle[];
  tourist_categories: TouristCode[];
  tourist_arrival_types: TouristCode[];
  place_mapping_suggestions: TouristMappingSuggestion[];
  article_mapping_suggestions: TouristMappingSuggestion[];
  category_mapping_suggestions: TouristMappingSuggestion[];
  arrival_type_mapping_suggestions: TouristMappingSuggestion[];
  place_mapping_issues: string[];
  article_mapping_issues: string[];
  missing_place_plaids?: number[];
  row_counts: Record<string, number>;
}

export interface TouristImportUploadSummary {
  id: string;
  filename: string;
  file_size_bytes: number | null;
  bytes_received: number;
  status: string;
  progress: TouristImportProgress;
  error_message: string | null;
  created_at: string;
  latest_preview_job_id: string | null;
  latest_preview_job_status: string | null;
}

export interface TouristImportUploadSession {
  upload: TouristImportUpload & { created_at: string };
  preview_job: TouristImportJob | null;
  /** Laufender Analyse- oder Import-Job (zum Fortsetzen nach Verbindungsabbruch). */
  active_job: TouristImportJob | null;
  mappings: {
    place_mappings: PlaceMappingPayload[];
    article_mappings: ArticleMappingPayload[];
    category_mappings?: CategoryMappingPayload[];
    arrival_type_mappings?: ArrivalTypeMappingPayload[];
    place_mapping_issues?: string[];
  };
}

export interface TouristImportReconcileArticlesResult {
  lines_updated: number;
  lines_scanned: number;
  mappings_available: number;
}

export interface TouristImportRunResult {
  job_id: string;
  status: string;
  stats: Record<string, number>;
  error_count: number;
}

export interface TouristImportError {
  id: string;
  source_table: string;
  source_row: number | null;
  source_id: string | null;
  field_name: string | null;
  message: string;
  created_at: string;
}

export interface TouristImportErrorSummary {
  source_table: string;
  message: string;
  count: number;
  field_name?: string | null;
  example_source_id?: string | null;
  example_source_row?: number | null;
}

export interface TouristImportErrorsPage {
  total: number;
  limit: number;
  offset: number;
  items: TouristImportError[];
}

export interface TouristImportErrorReport {
  total_errors: number;
  logged_samples: number;
  suppressed: number;
  place_mappings_used: number;
  summary: TouristImportErrorSummary[];
}

export interface TouristImportDatabaseCounts {
  guests: number;
  bookings_reservations: number;
  bookings_stays: number;
  bookings_pos: number;
  bookings_total: number;
  invoices: number;
  invoice_lines: number;
  payments: number;
  pitch_occupancies: number;
  license_plates: number;
}

export interface TouristImportSourceCount {
  source_table: string;
  label: string;
  count_in_database: number;
}

export interface TouristImportDiagnostics {
  bookings_missing_for_referenced_rec: number;
  last_job_bookings_inferred_stay: number;
  last_job_rec_date_errors: number;
  last_job_rec_place_errors: number;
  hints: string[];
}

export interface TouristImportStats {
  completed_import_jobs: number;
  in_database: TouristImportDatabaseCounts;
  by_source: TouristImportSourceCount[];
  last_completed_job: {
    job_id: string;
    finished_at: string | null;
    source_type: string;
    stats: Record<string, unknown>;
    error_count: number;
  } | null;
  reconcile_preview: {
    invoices_updated: number;
    invoices_scanned: number;
    invoices_with_lines: number;
    invoices_without_lines: number;
    total_revenue: string;
  } | null;
  diagnostics: TouristImportDiagnostics | null;
}

export interface TouristImportErrorSummaryItem {
  count: number;
  message: string;
  field_name?: string | null;
  source_table: string;
  example_source_id?: string | null;
  example_source_row?: number | null;
}

export interface TouristImportUpload {
  id: string;
  filename: string;
  file_size_bytes: number | null;
  bytes_received: number;
  status: string;
  progress: TouristImportProgress;
  error_message: string | null;
}

export interface TouristImportJob {
  id: string;
  upload_id: string | null;
  status: string;
  source_type: string;
  stats: Record<string, number>;
  preview_summary: TouristImportPreview | null;
  progress: TouristImportProgress;
  error_message: string | null;
}

export interface PlaceMappingPayload {
  tourist_plaid: number;
  tourist_planr: string;
  target_place_id: string;
}

export interface ArticleMappingPayload {
  tourist_artid: number;
  tourist_artnr?: string | null;
  tourist_bezeichnung?: string | null;
  target_article_id: string;
}

/** Tourist-Platzkategorie-Kürzel (adr14) → CampDesk-Platzkategorie. */
export interface CategoryMappingPayload {
  tourist_code: string;
  target_category_id: string;
}

/** Tourist-Anreiseart-Kürzel (adr22) → CampDesk-Anreiseart. */
export interface ArrivalTypeMappingPayload {
  tourist_code: string;
  target_arrival_type_id: string;
}

export interface TouristImportMappingsPayload {
  place_mappings: PlaceMappingPayload[];
  article_mappings: ArticleMappingPayload[];
  category_mappings?: CategoryMappingPayload[];
  arrival_type_mappings?: ArrivalTypeMappingPayload[];
}

function buildUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  if (!API_BASE) return path;
  return `${API_BASE}${path.startsWith("/") ? "" : "/"}${path}`;
}

async function parseJsonResponse(res: Response): Promise<unknown> {
  const ct = res.headers.get("content-type") || "";
  if (!ct.includes("application/json")) {
    const text = await res.text();
    return text || null;
  }
  try {
    return await res.json();
  } catch {
    return null;
  }
}

function extractErrorMessage(parsed: unknown, status: number, statusText: string): string {
  let message = `${status} ${statusText}`;
  if (parsed && typeof parsed === "object" && "detail" in parsed) {
    const detail = (parsed as { detail?: unknown }).detail;
    if (typeof detail === "string") message = detail;
    else if (detail && typeof detail === "object" && "message" in detail) {
      message = String((detail as { message: unknown }).message);
    } else if (detail) {
      message = JSON.stringify(detail);
    }
  }
  return message;
}

export async function startTouristSqlUploadSession(
  file: File,
): Promise<{ upload_id: string; progress: TouristImportProgress }> {
  const startForm = new FormData();
  startForm.append("filename", file.name);
  startForm.append("file_size_bytes", String(file.size));

  return api.post<{ upload_id: string; progress: TouristImportProgress }>(
    "/api/v1/admin/import/tourist/uploads",
    undefined,
    { rawBody: startForm },
  );
}

export function uploadTouristSqlFile(
  uploadId: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<TouristImportUpload> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const form = new FormData();
    form.append("file", file, file.name);

    xhr.upload.addEventListener("progress", (ev) => {
      if (ev.lengthComputable && onProgress) {
        onProgress(Math.min(99, Math.round((ev.loaded / ev.total) * 100)));
      }
    });

    xhr.addEventListener("load", () => {
      void (async () => {
        const parsed = await parseJsonResponse(
          new Response(xhr.responseText, {
            status: xhr.status,
            headers: { "Content-Type": xhr.getResponseHeader("content-type") ?? "" },
          }),
        );
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(parsed as TouristImportUpload);
          return;
        }
        reject(
          new ApiError(
            xhr.status,
            extractErrorMessage(parsed, xhr.status, xhr.statusText),
            parsed,
          ),
        );
      })();
    });

    xhr.addEventListener("error", () => {
      reject(
        new ApiError(
          0,
          "Verbindung beim Hochladen unterbrochen. Bitte Netzwerk prüfen und erneut versuchen.",
        ),
      );
    });

    xhr.addEventListener("abort", () => {
      reject(new ApiError(0, "Upload abgebrochen."));
    });

    xhr.open("POST", buildUrl(`/api/v1/admin/import/tourist/uploads/${uploadId}/file`));
    xhr.withCredentials = true;
    xhr.setRequestHeader("X-Tenant-Slug", tenantSlug());
    xhr.send(form);
  });
}

export async function getTouristUpload(uploadId: string): Promise<TouristImportUpload> {
  return api.get<TouristImportUpload>(`/api/v1/admin/import/tourist/uploads/${uploadId}`);
}

export async function listTouristUploads(): Promise<TouristImportUploadSummary[]> {
  return api.get<TouristImportUploadSummary[]>("/api/v1/admin/import/tourist/uploads");
}

export async function getTouristUploadSession(
  uploadId: string,
): Promise<TouristImportUploadSession> {
  return api.get<TouristImportUploadSession>(
    `/api/v1/admin/import/tourist/uploads/${uploadId}/session`,
  );
}

export async function getTouristImportJobMappings(jobId: string): Promise<TouristImportMappingsResult> {
  return api.get(`/api/v1/admin/import/tourist/jobs/${jobId}/mappings`);
}

function normalizePlaceMappingsPayload(
  mappings: PlaceMappingPayload[],
): PlaceMappingPayload[] {
  return mappings
    .filter((m) => m.target_place_id)
    .map(({ tourist_plaid, tourist_planr, target_place_id }) => ({
      tourist_plaid,
      tourist_planr: tourist_planr.slice(0, 64),
      target_place_id,
    }));
}

function normalizeCodeMappingsPayload<T extends { tourist_code: string }>(
  mappings: T[] | undefined,
): T[] {
  return (mappings ?? []).filter((m) => m.tourist_code.trim());
}

function normalizeMappingsPayload(
  mappings: TouristImportMappingsPayload,
): TouristImportMappingsPayload {
  return {
    place_mappings: normalizePlaceMappingsPayload(mappings.place_mappings),
    article_mappings: mappings.article_mappings,
    category_mappings: normalizeCodeMappingsPayload(mappings.category_mappings).filter(
      (m) => m.target_category_id,
    ),
    arrival_type_mappings: normalizeCodeMappingsPayload(mappings.arrival_type_mappings).filter(
      (m) => m.target_arrival_type_id,
    ),
  };
}

export async function saveTouristImportJobMappings(
  jobId: string,
  mappings: TouristImportMappingsPayload,
): Promise<TouristImportMappingsResult> {
  return api.put(
    `/api/v1/admin/import/tourist/jobs/${jobId}/mappings`,
    normalizeMappingsPayload(mappings),
  );
}

/** Speichert Zuordnungen am aktuellen Vorschau-Job des Uploads (robuster als feste job_id). */
export type TouristImportMappingsResult = {
  place_mappings: PlaceMappingPayload[];
  article_mappings: ArticleMappingPayload[];
  category_mappings?: CategoryMappingPayload[];
  arrival_type_mappings?: ArrivalTypeMappingPayload[];
  place_mapping_issues: string[];
};

export async function saveTouristUploadMappings(
  uploadId: string,
  mappings: TouristImportMappingsPayload,
): Promise<TouristImportMappingsResult> {
  return api.put(
    `/api/v1/admin/import/tourist/uploads/${uploadId}/mappings`,
    normalizeMappingsPayload(mappings),
  );
}

export async function startTouristPreview(
  uploadId: string,
  mappings?: TouristImportMappingsPayload,
): Promise<{ job_id: string; status: string; progress: TouristImportProgress }> {
  const form = new FormData();
  form.append("upload_id", uploadId);
  if (mappings) {
    form.append("mappings_json", JSON.stringify(normalizeMappingsPayload(mappings)));
  }
  return api.post(
    "/api/v1/admin/import/tourist/preview",
    undefined,
    { rawBody: form },
  );
}

export async function startTouristImport(
  uploadId: string,
  mappings: TouristImportMappingsPayload,
): Promise<{ job_id: string; status: string; progress: TouristImportProgress }> {
  const form = new FormData();
  form.append("upload_id", uploadId);
  form.append("mappings_json", JSON.stringify(normalizeMappingsPayload(mappings)));
  return api.post("/api/v1/admin/import/tourist/run", undefined, { rawBody: form });
}

export async function getTouristImportJob(jobId: string): Promise<TouristImportJob> {
  return api.get<TouristImportJob>(`/api/v1/admin/import/tourist/jobs/${jobId}`);
}

export async function cancelTouristImportJob(jobId: string): Promise<TouristImportJob> {
  return api.post<TouristImportJob>(
    `/api/v1/admin/import/tourist/jobs/${jobId}/cancel`,
    undefined,
  );
}

const POLL_TRANSIENT_STATUSES = new Set([0, 502, 503, 504]);

/** Max. Wartezeit zwischen Poll-Versuchen nach Verbindungsfehlern (ms). */
const POLL_TRANSIENT_BACKOFF_CAP_MS = 30_000;

function isTransientPollError(err: unknown): boolean {
  return err instanceof ApiError && POLL_TRANSIENT_STATUSES.has(err.status);
}

function transientPollBackoffMs(intervalMs: number, consecutiveFailures: number): number {
  const factor = Math.min(consecutiveFailures, 6);
  return Math.min(intervalMs * 2 ** factor, POLL_TRANSIENT_BACKOFF_CAP_MS);
}

export async function pollTouristImportJob(
  jobId: string,
  options: {
    untilStatuses: string[];
    intervalMs?: number;
    onUpdate?: (job: TouristImportJob) => void;
    /** Nur für Tests: nach N Verbindungsfehlern abbrechen. Produktion: unbegrenzt. */
    maxTransientRetries?: number;
    onTransientError?: (consecutiveFailures: number) => void;
  },
): Promise<TouristImportJob> {
  const interval = options.intervalMs ?? 800;
  const maxRetries = options.maxTransientRetries;
  let transientFailures = 0;

  for (;;) {
    try {
      const job = await getTouristImportJob(jobId);
      transientFailures = 0;
      options.onUpdate?.(job);
      if (options.untilStatuses.includes(job.status)) {
        return job;
      }
      if (job.status === "failed") {
        return job;
      }
    } catch (err) {
      if (!isTransientPollError(err)) {
        throw err;
      }
      if (maxRetries !== undefined && transientFailures >= maxRetries) {
        throw new ApiError(
          0,
          `Verbindung zum Server unterbrochen (${transientFailures + 1}×). Der Import-Job ${jobId.slice(0, 8)}… läuft ggf. weiter – Seite neu laden oder später erneut versuchen.`,
        );
      }
      transientFailures += 1;
      options.onTransientError?.(transientFailures);
      await new Promise((r) =>
        setTimeout(r, transientPollBackoffMs(interval, transientFailures)),
      );
      continue;
    }
    await new Promise((r) => setTimeout(r, interval));
  }
}

export async function listTouristImportErrors(
  jobId: string,
  params?: { limit?: number; offset?: number },
): Promise<TouristImportErrorsPage> {
  const query = new URLSearchParams();
  if (params?.limit != null) query.set("limit", String(params.limit));
  if (params?.offset != null) query.set("offset", String(params.offset));
  const suffix = query.size ? `?${query.toString()}` : "";
  return api.get<TouristImportErrorsPage>(
    `/api/v1/admin/import/tourist/jobs/${jobId}/errors${suffix}`,
  );
}

export async function summarizeTouristImportErrors(
  jobId: string,
): Promise<TouristImportErrorSummary[]> {
  return api.get<TouristImportErrorSummary[]>(
    `/api/v1/admin/import/tourist/jobs/${jobId}/errors/summary`,
  );
}

export async function getTouristImportErrorReport(
  jobId: string,
): Promise<TouristImportErrorReport> {
  return api.get<TouristImportErrorReport>(
    `/api/v1/admin/import/tourist/jobs/${jobId}/errors/report`,
  );
}

export async function getTouristImportStats(options?: {
  includeReconcile?: boolean;
  includeDiagnostics?: boolean;
}): Promise<TouristImportStats> {
  const params = new URLSearchParams();
  if (options?.includeReconcile === false) params.set("include_reconcile", "false");
  if (options?.includeDiagnostics === false) params.set("include_diagnostics", "false");
  const suffix = params.size ? `?${params.toString()}` : "";
  return api.get<TouristImportStats>(`/api/v1/admin/import/tourist/stats${suffix}`);
}

export async function reconcileTouristArticleMappings(): Promise<TouristImportReconcileArticlesResult> {
  return api.post<TouristImportReconcileArticlesResult>(
    "/api/v1/admin/import/tourist/reconcile-article-mappings",
  );
}

/** Fehlerzusammenfassung aus Job-Stats (nach Finalize) oder Fehlerbericht. */
export function parseJobErrorSummary(
  stats: Record<string, unknown> | undefined,
): TouristImportErrorSummary[] {
  const raw = stats?.error_summary;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((item): item is Record<string, unknown> => item !== null && typeof item === "object")
    .map((item) => ({
      source_table: String(item.source_table ?? ""),
      message: String(item.message ?? ""),
      count: Number(item.count ?? 0),
      field_name: item.field_name != null ? String(item.field_name) : null,
      example_source_id:
        item.example_source_id != null ? String(item.example_source_id) : null,
      example_source_row:
        item.example_source_row != null ? Number(item.example_source_row) : null,
    }));
}

/** Stellt sicher, dass Vorschaudaten aus älteren Jobs alle erwarteten Felder haben. */
export function normalizeTouristImportPreview(
  raw: TouristImportPreview | Record<string, unknown>,
): TouristImportPreview {
  const preview = raw as TouristImportPreview;
  return {
    job_id: String(preview.job_id ?? ""),
    files: preview.files ?? [],
    warnings: preview.warnings ?? [],
    tourist_places: preview.tourist_places ?? [],
    tourist_articles: preview.tourist_articles ?? [],
    tourist_categories: preview.tourist_categories ?? [],
    tourist_arrival_types: preview.tourist_arrival_types ?? [],
    place_mapping_suggestions: preview.place_mapping_suggestions ?? [],
    article_mapping_suggestions: preview.article_mapping_suggestions ?? [],
    category_mapping_suggestions: preview.category_mapping_suggestions ?? [],
    arrival_type_mapping_suggestions: preview.arrival_type_mapping_suggestions ?? [],
    place_mapping_issues: preview.place_mapping_issues ?? [],
    article_mapping_issues: preview.article_mapping_issues ?? [],
    missing_place_plaids: preview.missing_place_plaids ?? [],
    row_counts: preview.row_counts ?? {},
  };
}

/** Normalisiert UUIDs für <select value=…> (Groß-/Kleinschreibung). */
export function normalizePlaceId(id: string | null | undefined): string {
  return id ? String(id).toLowerCase() : "";
}

/** @deprecated Nur für kleine Direkt-Uploads; große Dumps: startTouristSqlUpload + startTouristPreview */
export async function previewTouristSqlImport(
  file: File,
  mappings?: {
    place_mappings: PlaceMappingPayload[];
    article_mappings: ArticleMappingPayload[];
  },
): Promise<TouristImportPreview> {
  const form = new FormData();
  form.append("files", file, file.name);
  if (mappings) {
    form.append("mappings_json", JSON.stringify(mappings));
  }
  return api.post<TouristImportPreview>(
    "/api/v1/admin/import/tourist/preview",
    undefined,
    { rawBody: form },
  );
}
